import React, { useRef, useState, useEffect } from 'react';
import { Subtitle, TokenUsage } from '../types';
import { Clock, ZoomIn, ZoomOut, Settings2, X, ArrowLeftRight, Play, Pause, FastForward, Rewind, Activity, Wand2, RefreshCcw, AlertTriangle, Music } from 'lucide-react';
import { calculateAICost, shiftSubtitles, elasticSyncSubtitles, parseTimeToSeconds } from '../utils';
import WaveSurfer from 'wavesurfer.js';

interface SubtitleEditorProps {
  subtitles: Subtitle[];
  detectedLanguage: string;
  usage?: TokenUsage;
  videoUrl: string | null;
  videoDuration: number;
  onUpdateSubtitle: (id: number, field: 'text' | 'translatedText', value: string) => void;
  onBulkUpdate?: (subtitles: Subtitle[]) => void;
}

const SubtitleEditor: React.FC<SubtitleEditorProps> = ({ 
  subtitles, 
  detectedLanguage, 
  usage, 
  videoUrl,
  videoDuration,
  onUpdateSubtitle,
  onBulkUpdate
}) => {
  const estimatedCost = usage ? calculateAICost(usage) : null;
  
  // Refs
  const waveformContainerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const fallbackAudioRef = useRef<HTMLAudioElement>(null);
  
  // State
  const [currentTime, setCurrentTime] = useState(0);
  const [zoom, setZoom] = useState(50);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  
  // Sync States
  const [offsetValue, setOffsetValue] = useState<number>(0);
  const [elasticStart, setElasticStart] = useState<number>(0);
  const [elasticEnd, setElasticEnd] = useState<number>(videoDuration);
  const [syncMode, setSyncMode] = useState<'shift' | 'elastic'>('shift');

  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Initialize WaveSurfer
  useEffect(() => {
    if (!waveformContainerRef.current || !videoUrl) return;

    // Reset states
    setLoadError(null);
    setIsReady(false);
    setUseFallback(false);

    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }

    try {
      const ws = WaveSurfer.create({
        container: waveformContainerRef.current,
        waveColor: 'rgba(59, 130, 246, 0.4)',
        progressColor: '#60a5fa',
        cursorColor: '#ef4444',
        barWidth: 2,
        barGap: 3,
        barRadius: 3,
        height: 128,
        normalize: true,
        minPxPerSec: zoom,
        fillParent: true,
        interact: true,
        autoplay: false,
        fetchParams: {
          mode: 'cors',
          credentials: 'omit',
        }
      });

      // Handle loading promise specifically to catch CORS errors
      ws.load(videoUrl).catch((err) => {
        console.warn("WaveSurfer Load Error (switching to fallback):", err);
        setLoadError("Waveform unavailable (CORS). Switched to basic player.");
        setUseFallback(true);
      });

      ws.on('ready', () => {
        setIsReady(true);
        setLoadError(null);
        // Initialize elastic inputs with current subtitle boundaries
        if (subtitles.length > 0) {
          setElasticStart(parseTimeToSeconds(subtitles[0].startTime));
          setElasticEnd(parseTimeToSeconds(subtitles[subtitles.length-1].endTime));
        }
      });

      ws.on('error', (err) => {
         console.error("WaveSurfer Event Error:", err);
         // Only set error if not already ready (minor seek errors shouldn't block UI)
         if (!isReady) {
             setLoadError("Visualizer blocked. Using basic player.");
             setUseFallback(true);
         }
      });

      ws.on('audioprocess', (time) => {
        setCurrentTime(time);
      });

      ws.on('seek', (time) => {
        setCurrentTime(time);
      });

      ws.on('play', () => setIsPlaying(true));
      ws.on('pause', () => setIsPlaying(false));
      ws.on('finish', () => setIsPlaying(false));

      wavesurferRef.current = ws;

    } catch (e: any) {
      console.error("WaveSurfer Init Error:", e);
      setLoadError("Failed to initialize visualizer.");
      setUseFallback(true);
    }

    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
    };
  }, [videoUrl]);

  useEffect(() => {
    if (wavesurferRef.current && isReady && !useFallback) {
      wavesurferRef.current.zoom(zoom);
    }
  }, [zoom, isReady, useFallback]);

  // Sync scroll of list with playback
  useEffect(() => {
    if (isPlaying && listContainerRef.current) {
         const active = subtitles.find(sub => {
            const start = parseTimeToSeconds(sub.startTime);
            const end = parseTimeToSeconds(sub.endTime);
            return currentTime >= start && currentTime < end;
         });
         
         if (active) {
             const el = document.getElementById(`sub-${active.id}`);
             if (el) {
                 el.scrollIntoView({ behavior: 'smooth', block: 'center' });
             }
         }
    }
  }, [currentTime, isPlaying, subtitles]);

  const togglePlay = () => {
    if (useFallback && fallbackAudioRef.current) {
        if (fallbackAudioRef.current.paused) {
            fallbackAudioRef.current.play().catch(e => console.error("Playback error:", e));
        } else {
            fallbackAudioRef.current.pause();
        }
    } else if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const skip = (seconds: number) => {
    if (useFallback && fallbackAudioRef.current) {
        fallbackAudioRef.current.currentTime = Math.min(Math.max(0, fallbackAudioRef.current.currentTime + seconds), videoDuration);
    } else if (wavesurferRef.current) {
      wavesurferRef.current.skip(seconds);
    }
  };

  const applySync = () => {
    if (!onBulkUpdate) return;

    if (syncMode === 'shift') {
        if (offsetValue !== 0) {
            const newSubs = shiftSubtitles(subtitles, offsetValue);
            onBulkUpdate(newSubs);
        }
    } else {
        // Elastic Sync
        const newSubs = elasticSyncSubtitles(subtitles, elasticStart, elasticEnd);
        onBulkUpdate(newSubs);
    }
    setShowSyncModal(false);
  };

  const activeSub = subtitles.find(sub => {
    const start = parseTimeToSeconds(sub.startTime);
    const end = parseTimeToSeconds(sub.endTime);
    return currentTime >= start && currentTime < end;
  });

  return (
    <div className="flex flex-col gap-6">
      
      {/* 1. AUDIO VISUALIZER / PLAYER AREA */}
      <div className="w-full bg-gray-900/60 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl relative">
          
          {/* Header / Toolbar */}
          <div className="flex justify-between items-center p-4 border-b border-white/5 bg-gray-950/30">
              <div className="flex items-center gap-3">
                 <div className="p-2 bg-blue-500/20 rounded-lg animate-pulse">
                    <Activity className="w-5 h-5 text-blue-400" />
                 </div>
                 <div>
                    <h3 className="text-sm font-bold text-gray-200">{useFallback ? "Basic Audio Player" : "Audio Sync View"}</h3>
                    <p className="text-[10px] text-gray-500 font-mono">
                      {new Date(currentTime * 1000).toISOString().substr(14, 5)} / {new Date(videoDuration * 1000).toISOString().substr(14, 5)}
                    </p>
                 </div>
              </div>

              <div className="flex items-center gap-2">
                 <button 
                    onClick={() => {
                        if (subtitles.length > 0) {
                            setElasticStart(parseTimeToSeconds(subtitles[0].startTime));
                            setElasticEnd(parseTimeToSeconds(subtitles[subtitles.length-1].endTime));
                        }
                        setShowSyncModal(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 rounded-lg text-xs font-medium border border-blue-500/30 transition-colors shadow-lg shadow-blue-900/10"
                  >
                    <Wand2 className="w-3 h-3" /> Magic Sync
                  </button>
                  
                  {!useFallback && (
                    <>
                        <div className="h-6 w-px bg-gray-700 mx-1"></div>
                        <button onClick={() => setZoom(Math.max(10, zoom - 20))} className="p-1.5 text-gray-400 hover:text-white"><ZoomOut className="w-4 h-4" /></button>
                        <button onClick={() => setZoom(Math.min(200, zoom + 20))} className="p-1.5 text-gray-400 hover:text-white"><ZoomIn className="w-4 h-4" /></button>
                    </>
                  )}
              </div>
          </div>

          {/* TELEPROMPTER / ACTIVE TEXT */}
          <div className="w-full min-h-[160px] flex items-center justify-center p-8 bg-gradient-to-b from-transparent to-gray-950/20">
              {activeSub ? (
                 <div className="text-center space-y-2 animate-in slide-in-from-bottom-2 duration-300">
                    <h2 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-100 to-blue-300 drop-shadow-md">
                       {activeSub.text}
                    </h2>
                    {activeSub.translatedText && (
                      <p className="text-lg md:text-xl text-blue-400/80 font-medium">
                        {activeSub.translatedText}
                      </p>
                    )}
                 </div>
              ) : (
                 <p className="text-gray-600 text-lg italic">Waiting for speech...</p>
              )}
          </div>

          {/* WAVEFORM OR FALLBACK CONTAINER */}
          <div className="relative w-full h-32 bg-gray-950/50 border-t border-white/5 group flex items-center justify-center">
             {!isReady && !loadError && !useFallback && (
                <div className="absolute inset-0 flex items-center justify-center z-20 bg-gray-950/80">
                   <div className="flex flex-col items-center gap-2 text-blue-400">
                      <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs font-mono">Generating Waveform...</span>
                   </div>
                </div>
             )}
             
             {/* Fallback Player */}
             {useFallback && videoUrl && (
                 <div className="w-full px-8 animate-in fade-in duration-500">
                     <div className="flex items-center gap-3 mb-2 justify-center text-yellow-500/80 bg-yellow-500/10 py-1 px-3 rounded-full w-fit mx-auto border border-yellow-500/20">
                         <AlertTriangle className="w-3 h-3" />
                         <span className="text-[10px] font-medium">{loadError || "Basic Mode Active"}</span>
                     </div>
                     <audio 
                        ref={fallbackAudioRef}
                        src={videoUrl}
                        controls
                        className="w-full h-10 accent-blue-500"
                        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                        onError={(e) => console.error("Audio tag error:", e)}
                     />
                 </div>
             )}

             {/* Waveform Element (Hidden if fallback) */}
             <div 
                ref={waveformContainerRef} 
                className={`w-full h-full opacity-80 group-hover:opacity-100 transition-opacity ${useFallback ? 'hidden' : 'block'}`} 
             />
          </div>

          {/* PLAYER CONTROLS (Bottom Bar - Only for Waveform mode, or as extra controls) */}
          {!useFallback && (
              <div className="flex justify-center items-center gap-6 p-4 bg-gray-950/80 border-t border-white/5">
                  <button onClick={() => skip(-5)} className="text-gray-400 hover:text-white transition-colors"><Rewind className="w-5 h-5" /></button>
                  <button 
                    onClick={togglePlay}
                    disabled={!!loadError && !useFallback}
                    className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 ${loadError ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/40'}`}
                  >
                    {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
                  </button>
                  <button onClick={() => skip(5)} className="text-gray-400 hover:text-white transition-colors"><FastForward className="w-5 h-5" /></button>
              </div>
          )}
      </div>

      {/* 2. SYNC MODAL */}
      {showSyncModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowSyncModal(false)}></div>
              <div className="relative bg-gray-900 border border-white/10 rounded-3xl p-6 w-full max-w-md shadow-2xl animate-in zoom-in-95 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                  
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <RefreshCcw className="w-5 h-5 text-blue-400" /> Synchronization
                      </h3>
                      <button onClick={() => setShowSyncModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  
                  {/* TABS */}
                  <div className="flex bg-gray-800/50 p-1 rounded-xl mb-6">
                      <button 
                        onClick={() => setSyncMode('shift')}
                        className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${syncMode === 'shift' ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}
                      >
                          Simple Shift
                      </button>
                      <button 
                        onClick={() => setSyncMode('elastic')}
                        className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${syncMode === 'elastic' ? 'bg-blue-600 text-white shadow' : 'text-gray-400 hover:text-gray-200'}`}
                      >
                          <Wand2 className="w-3 h-3" /> Elastic Sync
                      </button>
                  </div>

                  {syncMode === 'shift' ? (
                     <div className="space-y-4 mb-6">
                        <p className="text-xs text-gray-400">Shift all subtitles forward or backward in time.</p>
                        <div className="flex items-center gap-4">
                            <button onClick={() => setOffsetValue(prev => Number((prev - 0.5).toFixed(1)))} className="p-2 bg-gray-800 rounded-lg text-gray-300">-0.5s</button>
                            <div className="flex-1 text-center font-mono text-xl font-bold text-blue-400">
                                {offsetValue > 0 ? '+' : ''}{offsetValue}s
                            </div>
                            <button onClick={() => setOffsetValue(prev => Number((prev + 0.5).toFixed(1)))} className="p-2 bg-gray-800 rounded-lg text-gray-300">+0.5s</button>
                        </div>
                     </div>
                  ) : (
                     <div className="space-y-4 mb-6">
                        <div className="bg-blue-900/20 border border-blue-500/20 p-3 rounded-lg text-xs text-blue-200">
                           <strong>How to use:</strong> Listen to the video. Enter the EXACT time the first word is spoken, and the EXACT time the last word ends. The system will mathematically stretch the subtitles to fit.
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">First Sub Starts At:</label>
                                <div className="flex items-center bg-gray-800 rounded-lg border border-gray-700">
                                    <input 
                                       type="number" step="0.1" 
                                       value={elasticStart} 
                                       onChange={(e) => setElasticStart(parseFloat(e.target.value))}
                                       className="w-full bg-transparent p-2 text-white font-mono text-sm outline-none"
                                    />
                                    <span className="pr-2 text-gray-500 text-xs">sec</span>
                                </div>
                                <button onClick={() => setElasticStart(currentTime)} className="text-[10px] text-blue-400 hover:underline mt-1">Use Current Time</button>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Last Sub Ends At:</label>
                                <div className="flex items-center bg-gray-800 rounded-lg border border-gray-700">
                                    <input 
                                       type="number" step="0.1" 
                                       value={elasticEnd} 
                                       onChange={(e) => setElasticEnd(parseFloat(e.target.value))}
                                       className="w-full bg-transparent p-2 text-white font-mono text-sm outline-none"
                                    />
                                    <span className="pr-2 text-gray-500 text-xs">sec</span>
                                </div>
                                <button onClick={() => setElasticEnd(currentTime)} className="text-[10px] text-blue-400 hover:underline mt-1">Use Current Time</button>
                            </div>
                        </div>
                     </div>
                  )}

                  <button 
                      onClick={applySync}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-xl shadow-lg transition-all"
                  >
                      Apply Correction
                  </button>
              </div>
          </div>
      )}

      {/* 3. LIST EDITOR */}
      <div className="flex flex-col bg-gray-900/40 backdrop-blur-md rounded-3xl border border-white/5 overflow-hidden shadow-xl min-h-[400px]">
        <div className="p-4 border-b border-white/5 bg-gray-900/50 flex justify-between items-center shrink-0">
            <h3 className="font-semibold text-white flex items-center gap-2">
               <Settings2 className="w-4 h-4 text-blue-400" />
               Subtitle Editor
            </h3>
            <div className="flex items-center gap-2 text-xs">
                {estimatedCost && <span className="bg-emerald-900/30 text-emerald-300 px-2 py-1 rounded border border-emerald-800">{estimatedCost}</span>}
                <span className="bg-gray-800 text-gray-400 px-2 py-1 rounded border border-gray-700">{detectedLanguage}</span>
            </div>
        </div>

        <div ref={listContainerRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-950/30 custom-scrollbar max-h-[500px]">
            {subtitles.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
                <p>No subtitles generated yet.</p>
            </div>
            ) : (
            subtitles.map((sub) => {
                const isActive = activeSub?.id === sub.id;
                return (
                <div 
                    id={`sub-${sub.id}`}
                    key={sub.id} 
                    className={`
                    flex flex-col md:flex-row gap-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group
                    ${isActive 
                        ? 'bg-blue-900/10 border-blue-500/40 shadow-lg shadow-blue-900/5 border-l-4 border-l-blue-500' 
                        : 'bg-gray-800/20 border-white/5 hover:bg-gray-800/40'}
                    `}
                    onClick={() => {
                        const time = parseTimeToSeconds(sub.startTime);
                        if (useFallback && fallbackAudioRef.current) {
                            fallbackAudioRef.current.currentTime = time;
                            fallbackAudioRef.current.play();
                        } else if (wavesurferRef.current) {
                            wavesurferRef.current.setTime(time);
                            wavesurferRef.current.play();
                        }
                    }}
                >
                    <div className="w-full md:w-32 shrink-0 flex flex-row md:flex-col justify-between md:justify-start gap-2">
                        <div className="flex items-center gap-2">
                           <span className="text-xs font-mono text-gray-500">#{sub.id}</span>
                           <button className="md:hidden p-1 text-blue-400"><Play className="w-3 h-3" /></button>
                        </div>
                        <div className="text-xs font-mono text-gray-400 flex flex-col gap-1">
                            <span className="text-blue-400 bg-blue-900/20 px-1 rounded w-fit">{sub.startTime.split(',')[0]}</span>
                            <span className="opacity-50 text-[10px] pl-1">to</span>
                            <span className="text-gray-300 bg-gray-800 px-1 rounded w-fit">{sub.endTime.split(',')[0]}</span>
                        </div>
                    </div>

                    <div className="flex-1 grid grid-cols-1 gap-3">
                        <div className="relative group/edit">
                             <label className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1 block">Original</label>
                            <textarea 
                                className={`w-full bg-transparent text-sm border border-gray-700/50 rounded-lg p-2 min-h-[50px] resize-none transition-all outline-none focus:border-blue-500 focus:bg-gray-900/50 ${isActive ? 'text-white' : 'text-gray-300'}`}
                                value={sub.text}
                                onChange={(e) => onUpdateSubtitle(sub.id, 'text', e.target.value)}
                                onClick={(e) => e.stopPropagation()} // Prevent seek when editing text
                            />
                        </div>
                        {sub.translatedText !== undefined && (
                            <div className="relative group/edit">
                                <label className="text-[10px] text-blue-500/70 uppercase font-bold tracking-wider mb-1 block">Translation</label>
                                <textarea 
                                    className="w-full bg-transparent text-blue-300 text-sm border border-blue-900/30 rounded-lg p-2 min-h-[50px] resize-none transition-all outline-none focus:border-blue-500 focus:bg-blue-900/10"
                                    value={sub.translatedText}
                                    onChange={(e) => onUpdateSubtitle(sub.id, 'translatedText', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        )}
                    </div>
                </div>
                );
            })
            )}
        </div>
      </div>
    </div>
  );
};

export default SubtitleEditor;